// Turns the raw phone roll angle into a normalised steering input in [-1, 1]:
// calibration, smoothing, dead zone, response curve and drift correction.

import { param } from '../config/params';
import { wrapAngle } from './orientation';

const DEG = Math.PI / 180;

export const steeringParams = {
  range: param({ id: 'steer.rangeDeg', label: 'Full lock at (deg)', group: 'Steering', min: 20, max: 90, step: 1, value: 60 }),
  deadZone: param({ id: 'steer.deadZone', label: 'Dead zone', group: 'Steering', min: 0, max: 0.2, step: 0.005, value: 0.03 }),
  curve: param({ id: 'steer.curve', label: 'Response curve (exp)', group: 'Steering', min: 1, max: 2.5, step: 0.05, value: 1.3 }),
  minCutoff: param({ id: 'steer.minCutoff', label: 'Smoothing min cutoff (Hz)', group: 'Steering', min: 0.1, max: 10, step: 0.1, value: 1.5 }),
  beta: param({ id: 'steer.beta', label: 'Smoothing speed coeff', group: 'Steering', min: 0, max: 2, step: 0.01, value: 0.3 }),
  driftRate: param({ id: 'steer.driftRateDeg', label: 'Auto re-centre (deg/s)', group: 'Steering', min: 0, max: 3, step: 0.1, value: 0.5 }),
  driftWindow: param({ id: 'steer.driftWindowDeg', label: 'Auto re-centre window (deg)', group: 'Steering', min: 0, max: 10, step: 0.5, value: 4 }),
};

/**
 * One Euro filter (Casiez et al. 2012): low jitter when the signal is steady,
 * low lag when it moves fast. Well suited to noisy sensor input.
 */
export class OneEuroFilter {
  private x: number | undefined;
  private dx = 0;

  constructor(
    public minCutoff: number,
    public beta: number,
    public dCutoff = 1,
  ) {}

  reset(): void {
    this.x = undefined;
    this.dx = 0;
  }

  filter(value: number, dt: number): number {
    if (this.x === undefined || dt <= 0) {
      this.x = value;
      return value;
    }
    const rawDx = (value - this.x) / dt;
    this.dx += alpha(dt, this.dCutoff) * (rawDx - this.dx);
    const cutoff = this.minCutoff + this.beta * Math.abs(this.dx);
    this.x += alpha(dt, cutoff) * (value - this.x);
    return this.x;
  }
}

function alpha(dt: number, cutoff: number): number {
  const tau = 1 / (2 * Math.PI * cutoff);
  return 1 / (1 + tau / dt);
}

/** Dead zone, then a power curve for finer control near centre. Input and output in [-1, 1]. */
export function shapeInput(n: number, deadZone: number, exponent: number): number {
  const mag = Math.min(1, Math.abs(n));
  if (mag <= deadZone) return 0;
  const scaled = (mag - deadZone) / (1 - deadZone);
  return Math.sign(n) * Math.pow(scaled, exponent);
}

export interface DriftContext {
  /** True when the car is plainly driving straight (high speed, low yaw rate). */
  drivingStraight: boolean;
}

export class SteeringProcessor {
  private centre = 0;
  private lastRoll = 0;
  private hasReading = false;
  private filter = new OneEuroFilter(steeringParams.minCutoff.value, steeringParams.beta.value);
  autoDriftCorrection = true;

  /** Smoothed roll relative to centre, radians (before dead zone/curve). Used by the camera. */
  relativeRoll = 0;
  /** Final steering input in [-1, 1]. */
  output = 0;

  /** Feed a new raw roll reading (radians). */
  update(rawRoll: number, dt: number, ctx: DriftContext): number {
    if (!this.hasReading) {
      this.hasReading = true;
      this.centre = rawRoll;
    }
    this.lastRoll = rawRoll;
    this.filter.minCutoff = steeringParams.minCutoff.value;
    this.filter.beta = steeringParams.beta.value;

    const rel = this.filter.filter(wrapAngle(rawRoll - this.centre), dt);

    if (this.autoDriftCorrection && ctx.drivingStraight) {
      const window = steeringParams.driftWindow.value * DEG;
      if (Math.abs(rel) < window) {
        // Nudge the centre towards where the player is holding the phone.
        const maxStep = steeringParams.driftRate.value * DEG * dt;
        const step = Math.max(-maxStep, Math.min(maxStep, rel));
        this.centre = wrapAngle(this.centre + step);
      }
    }

    this.relativeRoll = rel;
    const n = rel / (steeringParams.range.value * DEG);
    this.output = shapeInput(n, steeringParams.deadZone.value, steeringParams.curve.value);
    return this.output;
  }

  /** Treat the current phone position as straight ahead. */
  recentre(): void {
    this.centre = this.lastRoll;
    this.filter.reset();
    this.relativeRoll = 0;
    this.output = 0;
  }
}
